import AddIcon from "@mui/icons-material/Add";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AnalyticsEvents, mixpanel } from "common/analytics";
import { ProfileIcon } from "common/icons";
import { useTranslation } from "common/locales";
import ProfileDialog from "features/userProfile/Profile";
import { Farm, useCurrentFarm, useUserFarms } from "model";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { switchFarm } from "services";
import { useAppStore, useUIStore } from "stores";
import FarmMenu from "./FarmMenu";

interface FarmBottomSheetProps {
  children: ReactNode;
}

export default function FarmBottomSheet({ children }: FarmBottomSheetProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { t } = useTranslation();

  // Check if we're on a detail page that has its own header
  const isDetailPage =
    location.pathname.startsWith("/animal-group/") ||
    location.pathname.startsWith("/field/") ||
    location.pathname.startsWith("/fields-list/");

  // Farm data
  const { data: currentFarm } = useCurrentFarm();
  const { data: allFarms } = useUserFarms();
  const user = useAppStore((state) => state.user);

  // Farm switcher menu state
  const [farmSwitchAnchor, setFarmSwitchAnchor] = useState<HTMLElement | null>(
    null
  );
  const [isSwitching, setIsSwitching] = useState(false);

  // Drag state for mobile bottom sheet
  const isExpanded = useUIStore((state) => state.mobileBottomSheetExpanded);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [startY, setStartY] = useState(0);
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(
    null
  );
  const [isAtScrollTop, setIsAtScrollTop] = useState(true);
  const [lockedScrollTop, setLockedScrollTop] = useState<number | null>(null);
  const [bottomSheetRef, setBottomSheetRef] = useState<HTMLDivElement | null>(
    null
  );
  const [handleRef, setHandleRef] = useState<HTMLDivElement | null>(null);

  // Drag handlers (native event handlers)
  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      const isHandle = target.closest('[data-handle="true"]');

      // Always allow drag from handle
      if (isHandle) {
        setIsDragging(true);
        setStartY(e.touches[0].clientY);
        // Lock scroll position when starting drag
        if (scrollContainer) {
          setLockedScrollTop(scrollContainer.scrollTop);
        }
        return;
      }

      // When collapsed, allow drag gestures everywhere
      if (!isExpanded) {
        setIsDragging(true);
        setStartY(e.touches[0].clientY);
        // Lock scroll position when starting drag
        if (scrollContainer) {
          setLockedScrollTop(scrollContainer.scrollTop);
        }
        return;
      }

      // When expanded, only start drag detection if we're at the top of scroll
      if (isExpanded && isAtScrollTop) {
        // Just store the start position, don't set dragging yet
        setStartY(e.touches[0].clientY);
        if (scrollContainer) {
          setLockedScrollTop(scrollContainer.scrollTop);
        }
      }
    },
    [isExpanded, scrollContainer, isAtScrollTop]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      const currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;

      // If we're not dragging yet, check if we should start
      if (!isDragging) {
        // Only consider starting drag when expanded and at top, and moving down
        if (isExpanded && isAtScrollTop && deltaY > 10) {
          // User is trying to drag down from top - start dragging
          setIsDragging(true);
          setDragOffset(deltaY);
          // Only prevent default if we can - if not, the drag will still work but might conflict with scroll
          if (e.cancelable) {
            e.preventDefault();
          }
        }
        // Otherwise, let normal scroll behavior handle it
        return;
      }

      // Now handle active dragging

      // When collapsed, only allow drag up
      if (!isExpanded) {
        if (deltaY < 0) {
          setDragOffset(deltaY);
          // Prevent default scrolling when dragging up from collapsed state
          if (e.cancelable) {
            e.preventDefault();
          }
        }
        return;
      }

      // When expanded, handle drag down logic
      if (isExpanded && deltaY > 0) {
        // Use the locked scroll position to determine if we were at top
        const wasAtTop = (lockedScrollTop || 0) <= 5;

        if (wasAtTop) {
          // Allow drag down when we started at top
          setDragOffset(deltaY);
          // Try to prevent scrolling to allow smooth dragging
          if (e.cancelable) {
            e.preventDefault();
          }
        } else {
          // Stop dragging to allow normal scrolling
          setIsDragging(false);
          setDragOffset(0);
          setLockedScrollTop(null);
          // Restore scroll position
          if (scrollContainer && lockedScrollTop !== null) {
            scrollContainer.scrollTop = lockedScrollTop;
          }
        }
      }
    },
    [
      isDragging,
      startY,
      isExpanded,
      scrollContainer,
      lockedScrollTop,
      isAtScrollTop,
    ]
  );

  const handleTouchEnd = useCallback(() => {
    // Always clean up, even if we weren't actively dragging
    setIsDragging(false);
    setStartY(0);
    setLockedScrollTop(null);

    // Only apply snap logic if we were actually dragging
    if (!isDragging) {
      setDragOffset(0);
      return;
    }

    // Snap logic based on drag distance
    const snapThreshold = 100; // pixels

    if (!isExpanded && dragOffset < -snapThreshold) {
      // Dragged up enough when collapsed - expand
      useUIStore.getState().expandMobileBottomSheet();
    } else if (isExpanded && dragOffset > snapThreshold) {
      // Dragged down enough when expanded - collapse
      useUIStore.getState().collapseMobileBottomSheet();
      // Reset scroll position when collapsing
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
    }

    // Reset drag offset with animation
    setDragOffset(0);
  }, [isDragging, dragOffset, isExpanded, scrollContainer]);

  // Register native touch event listeners with { passive: false }
  useEffect(() => {
    if (!bottomSheetRef || !handleRef) return;

    // Always attach non-passive listeners to the handle for reliable drag detection
    handleRef.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    handleRef.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    handleRef.addEventListener("touchend", handleTouchEnd, {
      passive: false,
    });

    // Attach listeners to the bottom sheet for collapsed state (when entire sheet is draggable)
    bottomSheetRef.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    bottomSheetRef.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    bottomSheetRef.addEventListener("touchend", handleTouchEnd, {
      passive: false,
    });

    return () => {
      handleRef.removeEventListener("touchstart", handleTouchStart);
      handleRef.removeEventListener("touchmove", handleTouchMove);
      handleRef.removeEventListener("touchend", handleTouchEnd);
      bottomSheetRef.removeEventListener("touchstart", handleTouchStart);
      bottomSheetRef.removeEventListener("touchmove", handleTouchMove);
      bottomSheetRef.removeEventListener("touchend", handleTouchEnd);
    };
  }, [
    bottomSheetRef,
    handleRef,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  ]);

  // Track scroll position
  useEffect(() => {
    if (!scrollContainer) return;

    const handleScroll = () => {
      // Don't update scroll position tracking when locked
      if (lockedScrollTop !== null) return;

      const isAtTop = scrollContainer.scrollTop <= 5; // Allow small tolerance
      setIsAtScrollTop(isAtTop);
    };

    // Set initial state
    const initialIsAtTop = scrollContainer.scrollTop <= 5;
    setIsAtScrollTop(initialIsAtTop);

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [scrollContainer, lockedScrollTop]);

  // Reset scroll position when collapsing
  useEffect(() => {
    if (!isExpanded && scrollContainer) {
      scrollContainer.scrollTop = 0;
    }
  }, [isExpanded, scrollContainer]);

  // Farm switcher handlers
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  const handleProfileClick = () => {
    setProfileDialogOpen(true);
  };

  const handleFarmSwitchClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!isSwitching) {
      setFarmSwitchAnchor(event.currentTarget);
    }
  };

  const handleFarmSwitchClose = () => {
    setFarmSwitchAnchor(null);
  };

  const handleSelectFarm = (farmId: string) => {
    if (farmId === currentFarm?.guid) return;

    setIsSwitching(true);
    setFarmSwitchAnchor(null);

    try {
      void switchFarm(farmId);
      mixpanel.track(AnalyticsEvents.FARM_SWITCHED);
      navigate(`/farm/${farmId}`, { replace: true });
    } catch (error) {
      console.error("Failed to switch farm:", error);
    } finally {
      setTimeout(() => setIsSwitching(false), 500);
    }
  };

  const handleAddNewFarm = () => {
    setFarmSwitchAnchor(null);
    mixpanel.track(AnalyticsEvents.FARM_ADD_STARTED);
    navigate("/add-farm/enter-name");
  };

  // Sort farms: own farms first, then consulting farms
  const byName = (l: Farm, b: Farm) => l.name.localeCompare(b.name);
  const ownFarms = (allFarms || [])
    .filter((f) => f.owner === user?.guid)
    .sort(byName);
  const consultingFarms = (allFarms || [])
    .filter((f) => f.owner !== user?.guid)
    .sort(byName);
  const sortedFarms = [...ownFarms, ...consultingFarms];

  return (
    <>
      <MobileBottomSheetContainer>
        <MobileBottomSheet
          ref={setBottomSheetRef}
          isExpanded={isExpanded}
          isDragging={isDragging}
          dragOffset={dragOffset}
        >
          <BottomSheetContent
            isExpanded={isExpanded}
            ref={setScrollContainer}
            data-scrollable="true"
            style={{
              overflowY: isDragging ? "hidden" : isExpanded ? "auto" : "hidden",
              touchAction: isDragging ? "none" : "auto",
            }}
          >
            <HandleContainer>
              <BottomSheetHandle ref={setHandleRef} data-handle="true" />
            </HandleContainer>

            {/* Farm Header - only show on farm overview, not on detail pages */}
            {!isDetailPage && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 16px 16px 16px",
                  backgroundColor: "white",
                  minHeight: 48,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: "text.primary",
                    flex: 1,
                  }}
                >
                  <Box
                    component="span"
                    sx={{ color: "text.secondary", fontWeight: 400 }}
                  >
                    {t("farm.prefix")}:{" "}
                  </Box>
                  {currentFarm?.name || t("farm.title")}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {/* Profile button */}
                  <IconButton
                    onClick={handleProfileClick}
                    size="small"
                    sx={{
                      backgroundColor: "#F1EFF7",
                      color: "#7D5DA3",
                      "&:hover": { backgroundColor: "#E5E1F0" },
                    }}
                  >
                    <ProfileIcon />
                  </IconButton>
                  {/* Add farm button - only show if single farm */}
                  {(allFarms || []).length === 1 && (
                    <IconButton
                      onClick={handleAddNewFarm}
                      size="small"
                      sx={{
                        backgroundColor: "#F1EFF7",
                        color: "#7D5DA3",
                        "&:hover": { backgroundColor: "#E5E1F0" },
                      }}
                      aria-label="Add farm"
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                  {/* Farm switch button - only show if multiple farms */}
                  {(allFarms || []).length > 1 && (
                    <IconButton
                      onClick={handleFarmSwitchClick}
                      size="small"
                      sx={{
                        backgroundColor: "#F1EFF7",
                        color: "#7D5DA3",
                        "&:hover": { backgroundColor: "#E5E1F0" },
                      }}
                    >
                      <SwapHorizIcon />
                    </IconButton>
                  )}
                  {/* Farm menu */}
                  {currentFarm && <FarmMenu farm={currentFarm} />}
                </Box>
              </Box>
            )}

            {children}
          </BottomSheetContent>
        </MobileBottomSheet>
      </MobileBottomSheetContainer>

      {/* Farm switch menu */}
      <Menu
        anchorEl={farmSwitchAnchor}
        open={Boolean(farmSwitchAnchor)}
        onClose={handleFarmSwitchClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            borderRadius: 2,
            minWidth: 200,
          },
        }}
      >
        {/* Add new Farm option */}
        <MenuItem
          onClick={handleAddNewFarm}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            py: 1.5,
            px: 2,
            mx: 1.5,
            my: 1,
            backgroundColor: "#2E7831",
            borderRadius: 2,
            color: "white",
            "&:hover": {
              backgroundColor: "#245C27",
            },
          }}
        >
          <AddCircleIcon sx={{ fontSize: 20, color: "white" }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: "white" }}>
            {t("farmSwitch.addNewFarm")}
          </Typography>
        </MenuItem>
        <Divider sx={{ my: 1 }} />
        {/* Header text */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: "0.75rem",
            }}
          >
            {t("farmSwitch.viewDataForDifferentSite")}
          </Typography>
        </Box>
        {/* Farm list */}
        {sortedFarms.map((farm) => {
          const isCurrentFarm = farm.guid === currentFarm?.guid;
          return (
            <MenuItem
              onClick={() => handleSelectFarm(farm.guid)}
              key={farm.guid}
              disabled={isCurrentFarm || isSwitching}
              sx={{
                py: 1.5,
                px: 2,
                opacity: isCurrentFarm ? 0.6 : 1,
              }}
            >
              <Typography variant="body2">
                {farm.name}
                {isCurrentFarm && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      ml: 1,
                      color: "text.secondary",
                      fontStyle: "italic",
                    }}
                  >
                    {t("farmSwitch.current")}
                  </Typography>
                )}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>
      <ProfileDialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
      />
    </>
  );
}

const MobileBottomSheetContainer = styled("div")({
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  height: "100vh",
  pointerEvents: "none",
  zIndex: 1200,
});

const MobileBottomSheet = styled("div")<{
  isExpanded: boolean;
  isDragging: boolean;
  dragOffset: number;
}>(({ dragOffset, isExpanded, isDragging }) => {
  const collapsedPosition = 70;
  const expandedPosition = 0;
  const basePosition = isExpanded ? expandedPosition : collapsedPosition;
  const dragPercentage = (Number(dragOffset) / window.innerHeight) * 100;

  let transformValue = `${basePosition}vh`;
  if (!isExpanded && dragOffset < 0) {
    transformValue = `${basePosition + dragPercentage}vh`;
  } else if (isExpanded && dragOffset > 0) {
    transformValue = `${basePosition + dragPercentage}vh`;
  }

  return {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    background: "white",
    borderRadius: "20px 20px 0 0",
    boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.1)",
    height: "calc(100vh - env(safe-area-inset-top))",
    transform: `translateY(${transformValue})`,
    transition: isDragging ? "none" : "transform 0.3s ease",
    overflow: "hidden",
    pointerEvents: "auto",
  };
});

const BottomSheetHandle = styled("div")({
  width: 40,
  height: 4,
  background: "#d1d5db",
  borderRadius: 2,
  cursor: "pointer",
});

const BottomSheetContent = styled("div")<{ isExpanded: boolean }>(
  ({ isExpanded }) => ({
    padding: 0,
    overflowY: isExpanded ? "auto" : "hidden",
    height: "calc(100vh - env(safe-area-inset-top) + 30px)",
    paddingBottom: "calc(env(safe-area-inset-bottom) + 100px)",
    paddingTop: 0,
    background: "white",
    display: "flex",
    flexDirection: "column",
  })
);

const HandleContainer = styled("div")({
  alignContent: "center",
  backgroundColor: "white",
  borderRadius: "20px 20px 0 0",
  height: 24,
  justifyContent: "center",
  width: "100%",
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
});
